// Central data for services, fleet, service areas, FAQ etc.
// Content-first architecture — anything visible on the marketing site is here or in the CMS.

export const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1507591064344-4c6ce005b128?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85',
  fleet_van: 'https://images.unsplash.com/photo-1746985094087-e703bf52c71b?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85',
  community_active: 'https://images.unsplash.com/photo-1569937728357-4971c45997c0?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85',
  support_care: 'https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85',
  aged_care: 'https://images.unsplash.com/photo-1781002219196-6627511653a3?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85',
  hospital: 'https://images.unsplash.com/photo-1709497197725-2e97c76b31d0?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85',
  // Additional service imagery (all rights-cleared Unsplash)
  driver_smile: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=1200&q=85',
  family_walk: 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=1400&q=85',
  senior_outside: 'https://images.unsplash.com/photo-1447452001602-7090c7ab2db3?w=1400&q=85',
  road_country: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1600&q=85',
  team_meeting: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1400&q=85',
  school_kids: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1400&q=85',
  medical_clean: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1400&q=85',
  city_route: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1600&q=85',
};

export const SERVICES = [
  {
    slug: 'ndis-transport',
    name: 'NDIS Transport',
    icon: 'Wheelchair',
    tagline: 'For every NDIS participant, every plan, every postcode.',
    summary: 'Door-to-door NDIS transport funded through Core, Capacity Building or Capital Supports — NDIS registration in progress, plan- and self-managed participants welcome today.',
    image: 'community_active',
    bullets: [
      'NDIS registration in progress — plan- and self-managed participants welcome now',
      'Direct invoicing, plan-managed or self-managed — we handle it all',
      'One-off, weekly or fully recurring bookings',
      'Support workers can travel with you at no extra vehicle cost',
    ],
    outcomes: [
      { label: 'Plan-friendly billing', body: 'Line items match your plan and support coordinator preferences.' },
      { label: 'Consistent drivers', body: 'Where possible we roster the same driver for recurring routes.' },
      { label: 'Escalation on demand', body: '24/7 duty coordinator for changes, cancellations or emergencies.' },
    ],
  },
  {
    slug: 'community-transport',
    name: 'Community Transport',
    icon: 'UsersThree',
    tagline: 'Connecting people to community, culture and everyday life.',
    summary: 'Shopping trips, social outings, cultural events and volunteer activities — reliable transport to help every Australian stay active in community life.',
    image: 'family_walk',
    bullets: [
      'One-off outings or weekly community routines',
      'Group transport for community programs and clubs',
      'Warm, familiar drivers who understand community care',
      'Coordinated pickups when travelling with support workers or family',
    ],
    outcomes: [
      { label: 'True door-to-door', body: 'From front door to venue and back — we do not do "kerb-side and go".' },
      { label: 'Flexible dwell time', body: 'Book waiting time or return-later trips to suit your day.' },
      { label: 'Group-friendly', body: 'From small carpools to full accessible mini-buses for community outings.' },
    ],
  },
  {
    slug: 'aged-care-transport',
    name: 'Aged Care Transport',
    icon: 'HandHeart',
    tagline: 'Dignified, unhurried transport for older Australians.',
    summary: 'Whether it\'s a hairdresser visit, family lunch or a medical appointment, aged care transport that treats every trip with the patience and respect it deserves.',
    image: 'aged_care',
    bullets: [
      'Home Care Package and CHSP compatible transport',
      'Trained in dementia-friendly communication',
      'Walker, wheelchair and oxygen tank ready',
      'Companion travel and appointment escort available',
    ],
    outcomes: [
      { label: 'Aged care providers', body: 'We partner with residential and in-home aged care operators nationally.' },
      { label: 'Family visibility', body: 'Family members receive optional pickup and drop-off notifications.' },
      { label: 'Respect for pace', body: 'No hurried transfers — dwell time is built into every job.' },
    ],
  },
  {
    slug: 'hospital-discharge',
    name: 'Hospital Discharge Transport',
    icon: 'FirstAid',
    tagline: 'Home from hospital — safely, any hour, any day.',
    summary: 'Same-day and after-hours hospital discharge transport, coordinated directly with ward staff, discharge planners and family.',
    image: 'hospital',
    bullets: [
      'Short-notice bookings, 24/7 dispatch',
      'Stretcher-alternative, wheelchair and standard vehicle options',
      'Direct coordination with hospital discharge planners',
      'Optional medication pick-up and pharmacy stop en route',
    ],
    outcomes: [
      { label: 'Ward-friendly workflow', body: 'Our team liaises with the ward so families don\'t have to.' },
      { label: 'After-hours capability', body: 'Late-night discharges? We routinely operate through the night.' },
      { label: 'Post-discharge welfare', body: 'Optional welfare check-in call the next day.' },
    ],
  },
  {
    slug: 'tac-worksafe',
    name: 'TAC & WorkSafe Transport',
    icon: 'ShieldCheck',
    tagline: 'Approved transport for TAC and WorkSafe recovery.',
    summary: 'Approved provider for Transport Accident Commission (TAC) and WorkSafe claimants — medical appointments, rehab and return-to-work travel.',
    image: 'medical_clean',
    bullets: [
      'Direct billing to TAC and WorkSafe where approved',
      'Clear communication with case managers',
      'Rehabilitation and specialist appointment routes',
      'Return-to-work travel support programs',
    ],
    outcomes: [
      { label: 'Compliant paperwork', body: 'Documentation that meets TAC and WorkSafe audit requirements.' },
      { label: 'Recovery-first', body: 'Drivers trained to support clients recovering from injury.' },
      { label: 'National coverage', body: 'Consistent service standards across every Australian state.' },
    ],
  },
  {
    slug: 'wheelchair-accessible',
    name: 'Wheelchair Accessible Transport',
    icon: 'WheelchairMotion',
    tagline: 'Modified vehicles. Trained drivers. Zero compromise.',
    summary: 'A modern fleet of purpose-built wheelchair accessible vehicles — hydraulic and ramp-loading, full tie-down safety, and drivers trained to the highest standard.',
    image: 'support_care',
    bullets: [
      'Rear-entry ramp and side-lift options',
      'ISO-compliant four-point tie-downs on every vehicle',
      'Space for power chairs, scooters and companion seating',
      'Regular safety inspections and driver refresher training',
    ],
    outcomes: [
      { label: 'Zero-transfer transport', body: 'Stay in your wheelchair — safer, faster, more dignified.' },
      { label: 'Power chair ready', body: 'Certified for larger power chairs up to industry weight limits.' },
      { label: 'Trained restraint use', body: 'Every driver is trained and re-certified on restraint systems.' },
    ],
  },
  {
    slug: 'medical-appointment',
    name: 'Medical Appointment Transport',
    icon: 'Stethoscope',
    tagline: 'On time, every time — for every appointment that matters.',
    summary: 'GP visits, specialists, dialysis, chemotherapy, physio and rehab — with waiting and return options built into every booking.',
    image: 'medical_clean',
    bullets: [
      'Same-day and pre-scheduled appointment transport',
      'Waiting time and return-trip bookings',
      'Dialysis and chemotherapy transport programs',
      'Coordination with clinic reception for late-running appointments',
    ],
    outcomes: [
      { label: 'On-time reliability', body: 'Our schedule builds in buffer time — clinics run late, we don\'t.' },
      { label: 'Recovery-aware', body: 'Drivers understand post-procedure care and are patient with recovery.' },
      { label: 'Family notifications', body: 'Optional SMS notifications when your loved one arrives and departs.' },
    ],
  },
  {
    slug: 'social-community-access',
    name: 'Social & Community Access',
    icon: 'SmileyMeh',
    tagline: 'Get out. Stay connected. Live well.',
    summary: 'Transport to support social, cultural, sporting and family events — because staying connected to community is part of staying well.',
    image: 'community_active',
    bullets: [
      'Cinema, restaurants, sports and family visits',
      'Cultural, faith and community events',
      'Group day trips and short excursions',
      'Coordinated with your support workers and coordinators',
    ],
    outcomes: [
      { label: 'Independence-first', body: 'Support that enables — never overrides — your choices.' },
      { label: 'Predictable pricing', body: 'Clear pricing so you can plan social outings with confidence.' },
      { label: 'Late-night pickups', body: 'Concerts and events end late — we\'re still on call.' },
    ],
  },
  {
    slug: 'school-education',
    name: 'School & Education Transport',
    icon: 'GraduationCap',
    tagline: 'Safe, reliable transport for every learner.',
    summary: 'Daily transport for students to schools, specialist schools, TAFE and training programs — with the safety standards families expect.',
    image: 'school_kids',
    bullets: [
      'Working-with-Children Check verified drivers',
      'Consistent driver rostering for routine and comfort',
      'Coordination with families, schools and support workers',
      'Specialist school and post-school transition transport',
    ],
    outcomes: [
      { label: 'WWCC on every driver', body: 'Every driver holds a current WWCC — no exceptions.' },
      { label: 'Parent portal', body: 'Optional parent SMS notifications on pickup and drop-off.' },
      { label: 'Routine that builds trust', body: 'The same driver, the same route, the same reassurance.' },
    ],
  },
  {
    slug: 'group-transport',
    name: 'Group Transport',
    icon: 'Van',
    tagline: 'Bigger groups. Same standard of care.',
    summary: 'Day programs, community groups, workplace excursions and event transport — small and large group vehicles across our fleet.',
    image: 'fleet_van',
    bullets: [
      '4-seat, 8-seat, 12-seat and mini-bus configurations',
      'Accessible group vehicles available',
      'Full-day, half-day and event-only bookings',
      'Coordination with venues and program managers',
    ],
    outcomes: [
      { label: 'Whole-of-program transport', body: 'Perfect for community programs and day services.' },
      { label: 'Event capability', body: 'Weddings, funerals, sporting fixtures — we handle them all.' },
      { label: 'Corporate & workplace', body: 'Available for workplace transport under commercial terms.' },
    ],
  },
];

export const FLEET = [
  { name: 'Accessible Wheelchair Van', type: 'Wheelchair', capacity: '1WC + 3', features: ['Rear-entry ramp', '4-point tie-downs', 'Grab-rails'], image: 'fleet_van' },
  { name: 'Side-Lift Accessible Vehicle', type: 'Wheelchair', capacity: '1WC + 2', features: ['Hydraulic side lift', 'Power-chair rated', 'Companion seat'], image: 'support_care' },
  { name: 'Community Sedan', type: 'Standard', capacity: '4', features: ['Comfort suspension', 'Boot for mobility aids', 'Child restraint ready'], image: 'driver_smile' },
  { name: 'Mid-Size People Mover', type: 'Standard', capacity: '7', features: ['Family-friendly', 'Group outings', 'Air conditioning'], image: 'family_walk' },
  { name: 'Group Mini-Bus', type: 'Group', capacity: '12', features: ['Community programs', 'Sports & excursions', 'Certified driver'], image: 'road_country' },
  { name: 'Accessible Mini-Bus', type: 'Group / Wheelchair', capacity: '2WC + 8', features: ['Two wheelchair spots', 'Full accessibility', 'Group day trips'], image: 'city_route' },
];

export const SERVICE_AREAS = {
  headline: 'Wherever you are in Australia, we\'re moving with you.',
  regions: [
    { state: 'Victoria', hubs: ['Melbourne metro', 'Geelong', 'Bendigo', 'Ballarat', 'Latrobe Valley'] },
    { state: 'Western Australia', hubs: ['Perth metro', 'Mandurah', 'Bunbury', 'Rockingham', 'Joondalup'] },
    { state: 'New South Wales', hubs: ['Sydney metro', 'Newcastle', 'Wollongong', 'Central Coast'] },
    { state: 'Queensland', hubs: ['Brisbane', 'Gold Coast', 'Sunshine Coast', 'Toowoomba'] },
    { state: 'South Australia', hubs: ['Adelaide metro', 'Mount Gambier', 'Whyalla'] },
    { state: 'Tasmania', hubs: ['Hobart', 'Launceston'] },
    { state: 'Northern Territory', hubs: ['Darwin', 'Alice Springs'] },
    { state: 'ACT', hubs: ['Canberra'] },
  ],
};

export const TRUST_POINTS = [
  { icon: 'ShieldCheck', title: 'NDIS Registration in Progress', body: 'Working towards full NDIS registration — plan- and self-managed participants can travel with us today.' },
  { icon: 'Certificate', title: 'Fully Insured', body: 'Comprehensive commercial passenger and public liability insurance nationwide.' },
  { icon: 'HandHeart', title: 'Driver Training', body: 'First-aid, disability awareness, dementia-friendly and restraint-certified drivers.' },
  { icon: 'ClockClockwise', title: '24/7 Availability', body: 'A duty coordinator on call every hour of every day — including public holidays.' },
  { icon: 'CarProfile', title: 'Vehicle Standards', body: 'Every vehicle inspected quarterly and cleaned between every job.' },
  { icon: 'Users', title: 'WWCC Verified', body: 'All drivers hold current WWCC and are police-checked annually.' },
];

export const VALUES = [
  { title: 'Independence', body: 'We provide the transport — you keep the choices. Nothing in our service overrides your voice.' },
  { title: 'Dignity', body: 'Every trip is delivered with the respect we\'d want for our own family. That\'s the whole standard.' },
  { title: 'Reliability', body: 'On-time performance is not a KPI — it is the entire promise. If we\'re late, we own it.' },
  { title: 'Community', body: 'We invest in the communities we operate in — hiring locally, giving back visibly, listening constantly.' },
];

export const FAQ = [
  { q: 'Are you a registered NDIS provider?', a: 'Not yet — our NDIS registration is currently in progress and we expect it to be finalised soon. In the meantime, self-managed and plan-managed participants can already travel with Inclusive Mobility Group using their funding.' },
  { q: 'Which parts of Australia do you cover?', a: 'We operate nationally across all Australian states and territories, with primary service hubs in Victoria and Western Australia. Coverage in regional areas is confirmed at booking.' },
  { q: 'How much do your services cost?', a: 'Pricing depends on service type, distance, time of day and funding source. NDIS-funded transport is priced according to the current NDIS Price Guide. Request an instant quote and we\'ll email a tailored estimate within one business day.' },
  { q: 'Can I book recurring transport?', a: 'Yes — weekly, fortnightly, monthly and fully custom recurring bookings are our specialty. You can specify recurring options directly on our online booking form.' },
  { q: 'Do you accept TAC and WorkSafe clients?', a: 'Yes. We are an approved TAC and WorkSafe transport provider with direct billing arrangements available for eligible claims.' },
  { q: 'How quickly can you respond to a booking?', a: 'For pre-scheduled trips, book anytime — same-day and after-hours bookings are also possible via our 24/7 duty coordinator. Hospital discharge transport is often actioned within the hour.' },
  { q: 'Are your vehicles wheelchair accessible?', a: 'A significant portion of our fleet is purpose-built wheelchair accessible with ramp and hydraulic-lift options. All accessible vehicles meet Australian Disability Standards for Accessible Public Transport.' },
  { q: 'Can support workers travel with the participant?', a: 'Absolutely. Support workers, family members, guardians and companion travellers can accompany participants at no additional vehicle cost.' },
];

export const CONTACT = {
  phone_display: '1300 645 262',
  phone_dial: '1300794388',
  email: 'hello@inclusivemobilitygroup.com.au',
  admin_email: 'admin@swiftcommunitytransport.com.au',
  hours: '24 hours a day, 7 days a week',
  hq: 'National operations · Australia',
};

export const NAV = {
  main: [
    { label: 'Home', to: '/' },
    { label: 'About', to: '/about' },
    { label: 'Services', to: '/services' },
    { label: 'Fleet', to: '/fleet' },
    { label: 'Service Areas', to: '/service-areas' },
    { label: 'Testimonials', to: '/testimonials' },
    { label: 'Contact', to: '/contact' },
  ],
};

export const SERVICE_SLUGS = SERVICES.map(s => s.slug);
